import React, { useState } from 'react';
import { usePage } from '@inertiajs/react';
import { useForm } from '@mantine/form';
import { TextInput, Select, MultiSelect, Checkbox, Button, Box, Title, Group, Text } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconUpload, IconX, IconPhoto, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import FormLayout from '../../components/FormLayout';
import ImageCropper from '../../components/ImageCropper';

interface NewAreaProps {
  countries: [string, number][];
  cities: [string, number][];
  artists: { id: number; name: string; avatar_url?: string | null }[];
  organizers: { id: number; name: string }[];
  errors?: string[];
  [key: string]: any; // Add index signature to satisfy PageProps constraint
}

interface AreaFormValues {
  name: string;
  slug: string;
  country_id: number | null;
  strip_area_suffix: boolean;
  city_ids: number[];
  artist_ids: number[];
  organizer_ids: number[];
}

export default function NewArea() {
  const { countries, cities, artists, organizers } = usePage<NewAreaProps>().props;

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [metaImageSrc, setMetaImageSrc] = useState<string | null>(null);
  const [metaCroppedBlob, setMetaCroppedBlob] = useState<Blob | null>(null);
  const [metaPreviewUrl, setMetaPreviewUrl] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<AreaFormValues>({
    initialValues: {
      name: '',
      slug: '',
      country_id: null,
      strip_area_suffix: false,
      city_ids: [],
      artist_ids: [],
      organizer_ids: [],
    },
    validate: {
      name: (val) => (val ? null : 'Name is required'),
      slug: (val) => (val ? null : 'Slug is required'),
      country_id: (val) => (val ? null : 'Country is required'),
    },
    validateInputOnChange: true,
  });

  function slugify(str: string): string {
    return str
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '');
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    form.setFieldValue('name', newName);
    form.setFieldValue('slug', slugify(newName));
  };

  // Primary Dropzone handlers
  const handleDrop = (files: File[]) => {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      if (typeof e.target?.result === 'string') setImageSrc(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCropped = (blob: Blob) => {
    setCroppedBlob(blob);
    setPreviewUrl(URL.createObjectURL(blob));
    setImageSrc(null);
  };

  const removePreview = () => {
    setPreviewUrl(null);
    setCroppedBlob(null);
  };

  // Meta Dropzone handlers
  const handleMetaDrop = (files: File[]) => {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      if (typeof e.target?.result === 'string') setMetaImageSrc(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleMetaCropped = (blob: Blob) => {
    setMetaCroppedBlob(blob);
    setMetaPreviewUrl(URL.createObjectURL(blob));
    setMetaImageSrc(null);
  };

  const removeMetaPreview = () => {
    setMetaPreviewUrl(null);
    setMetaCroppedBlob(null);
  };

  const handleSubmit = async (values: AreaFormValues) => {
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';
    const data = new FormData();

    data.append('area[name]', values.name);
    data.append('area[slug]', values.slug);
    data.append('area[country_id]', String(values.country_id));
    data.append('area[strip_area_suffix]', String(values.strip_area_suffix));
    values.city_ids.forEach((id) => data.append('area[city_ids][]', String(id)));
    if (croppedBlob) data.append('area[skyline_image]', croppedBlob, 'skyline.jpg');
    if (metaCroppedBlob) data.append('area[meta_image]', metaCroppedBlob, 'meta.jpg');
    values.artist_ids.forEach((id) => data.append('area[artist_ids][]', String(id)));
    values.organizer_ids.forEach((id) => data.append('area[organizer_ids][]', String(id)));

    try {
      const response = await fetch('/city', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: data,
      });

      const payload = await response.json().catch(() => ({}) as any);
      if (response.ok) {
        showNotification({
          color: 'green',
          title: 'Success',
          message: 'Area created!',
          icon: <IconCheck />,
          position: 'top-right',
        });
        setTimeout(() => (window.location.href = `/city/${values.slug}`), 800);
      } else {
        const errs = Array.isArray(payload.errors) ? payload.errors : [];
        if (errs.length) {
          showNotification({
            color: 'red',
            title: 'Error',
            message: errs.join(', '),
            icon: <IconAlertCircle />,
            position: 'top-right',
          });
        } else {
          showNotification({
            color: 'red',
            title: 'Validation Error',
            message: 'Please fix the highlighted fields.',
            icon: <IconAlertCircle />,
            position: 'top-right',
          });
        }
      }
    } catch (err) {
      console.error(err);
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'An unexpected error occurred.',
        icon: <IconAlertCircle />,
        position: 'top-right',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout title="Create A New Area">
      <Notifications />
      <Title order={2}>New Area</Title>

      {/* Skyline Image */}
      {!previewUrl && !imageSrc && (
        <Dropzone
          onDrop={handleDrop}
          onReject={() => {}}
          maxSize={5 * 1024 ** 2}
          accept={IMAGE_MIME_TYPE}
          styles={() => ({ root: { backgroundColor: '#fcefe1', border: '3px dashed #f6b73c' } })}
        >
          <Group justify="center" gap="xl" style={{ minHeight: 120, pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload size={48} stroke={1.5} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={48} stroke={1.5} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPhoto size={48} stroke={1.5} />
            </Dropzone.Idle>
            <div>
              <Text>Drag skyline image here or click to select</Text>
              <Text size="sm" color="dimmed">
                Max 5 MB
              </Text>
            </div>
          </Group>
        </Dropzone>
      )}
      {imageSrc && !previewUrl && <ImageCropper src={imageSrc} setCroppedBlob={handleCropped} aspectRatio={16 / 9} />}
      {previewUrl && (
        <Box mt="md">
          <img src={previewUrl} alt="Skyline preview" style={{ width: '100%', maxHeight: 200, objectFit: 'cover' }} />
          <Button variant="filled" color="red" mt="md" onClick={removePreview}>
            Remove
          </Button>
        </Box>
      )}

      {/* Meta Image */}
      {!metaPreviewUrl && !metaImageSrc && (
        <Box mt="lg">
          <Text fw={500} className="text-white">
            Meta Image (e.g. social card):
          </Text>
          <Dropzone
            onDrop={handleMetaDrop}
            onReject={() => {}}
            maxSize={5 * 1024 ** 2}
            accept={IMAGE_MIME_TYPE}
            styles={() => ({ root: { backgroundColor: '#e1f3fc', border: '3px dashed #3caef6' } })}
          >
            <Group justify="center" gap="xl" style={{ minHeight: 120 }}>
              <Dropzone.Accept>
                <IconUpload size={48} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={48} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto size={48} stroke={1.5} />
              </Dropzone.Idle>
              <div>
                <Text>Drag meta image here or click to select</Text>
                <Text size="sm" color="dimmed">
                  Max 5 MB
                </Text>
              </div>
            </Group>
          </Dropzone>
        </Box>
      )}
      {metaImageSrc && !metaPreviewUrl && (
        <ImageCropper src={metaImageSrc} setCroppedBlob={handleMetaCropped} aspectRatio={16 / 9} />
      )}
      {metaPreviewUrl && (
        <Box mt="md">
          <img src={metaPreviewUrl} alt="Meta preview" style={{ width: '100%', maxHeight: 200, objectFit: 'cover' }} />
          <Button variant="filled" color="red" mt="sm" onClick={removeMetaPreview}>
            Remove
          </Button>
        </Box>
      )}

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label="Name"
          placeholder="Enter area name"
          mt="md"
          {...form.getInputProps('name')}
          onChange={handleNameChange}
        />
        <TextInput label="Slug" placeholder="auto-generated" mt="sm" {...form.getInputProps('slug')} />
        <Select
          label="Country"
          placeholder="Select country"
          mt="sm"
          data={countries.map(([name, id]) => ({ value: String(id), label: name }))}
          {...form.getInputProps('country_id')}
          clearable
          searchable
        />
        <Checkbox
          label="Strip area suffix"
          mt="sm"
          {...form.getInputProps('strip_area_suffix', { type: 'checkbox' })}
        />
        <MultiSelect
          label="Cities"
          placeholder="Select cities"
          mt="sm"
          data={cities.map(([name, id]) => ({ value: String(id), label: name }))}
          {...form.getInputProps('city_ids')}
          searchable
          clearable
        />
        <MultiSelect
          label="Associated Artists"
          placeholder="Select artists"
          mt="sm"
          data={artists.map((a) => ({ value: String(a.id), label: a.name, image: a.avatar_url || undefined }))}
          {...form.getInputProps('artist_ids')}
          searchable
          clearable
        />
        <MultiSelect
          label="Associated Organizers"
          placeholder="Select organizers"
          mt="sm"
          data={organizers.map((o) => ({ value: String(o.id), label: o.name }))}
          {...form.getInputProps('organizer_ids')}
          searchable
          clearable
        />
        <Group justify="right" mt="xl">
          <Button type="submit" loading={isLoading}>
            Create Area
          </Button>
        </Group>
      </form>
    </FormLayout>
  );
}
